import React from "react";
import { Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import Helpers from "../../../../components/Helpers";
import uploadRetreat from "../../../../assets/images/uploadRetreat.png";
import corruptedImg from "../../../../assets/images/corrupted-img.png";
import type { LifecycleRetreat } from "../../../../types/retreatLifecycle";
import {
  formatLanguage,
  formatRetreatDateRange,
  isUpcomingRetreat,
} from "./lifecycleUi";
import {
  retreatDisplayTitle,
  retreatLocationLine,
} from "../manage/manageRetreatTypes";
import { RegistrationOpenRowIcons } from "../manage/RegistrationOpenRowIcons";

export const upcomingCellStyles = [
  {
    when: (row: LifecycleRetreat) => isUpcomingRetreat(row),
    style: { backgroundColor: "rgb(196 240 196)" },
  },
];

export const upcomingRowStyles = [
  {
    when: (row: LifecycleRetreat) => isUpcomingRetreat(row),
    style: { backgroundColor: "rgb(196 240 196)" },
  },
];

function cap(value?: string | null) {
  if (!value) return "";
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

function renderTooltip(text: React.ReactNode) {
  return <Tooltip id="button-tooltip">{text}</Tooltip>;
}

export function tooltipCell(text: React.ReactNode, tip?: string) {
  const label =
    tip ??
    (text == null || text === ""
      ? "—"
      : typeof text === "string" || typeof text === "number"
        ? String(text)
        : "—");
  return (
    <OverlayTrigger
      placement="bottom"
      delay={{ show: 250, hide: 400 }}
      overlay={renderTooltip(label || "—")}
    >
      <span style={{ display: "inline-block", maxWidth: "100%", cursor: "text" }}>
        {text == null || text === "" ? "—" : text}
      </span>
    </OverlayTrigger>
  );
}

export function fileViewCell(url?: string | null, label = "View file") {
  if (!url) return tooltipCell("—");
  return (
    <OverlayTrigger
      placement="bottom"
      delay={{ show: 250, hide: 400 }}
      overlay={renderTooltip(label)}
    >
      <a href={url} target="_blank" rel="noopener noreferrer" title={label}>
        <i className="mdi mdi-download" style={{ color: "#4cd964" }}></i>
      </a>
    </OverlayTrigger>
  );
}

function customTooltip(text: React.ReactNode) {
  return (
    <Tooltip id="button-tooltip" className="custom-tooltip">
      {text}
    </Tooltip>
  );
}

export function retreatTitleCell(row: LifecycleRetreat) {
  const title = retreatDisplayTitle(row);
  const location = retreatLocationLine(row);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "2px", paddingTop: "4px", paddingBottom: "4px" }}>
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 400 }}
        overlay={renderTooltip(title)}
      >
        <a
          href={`/retreatRoster?retreatId=${row.id}`}
          onClick={(e) => {
            e.preventDefault();
            window.open(`/retreatRoster?retreatId=${row.id}`, "_blank");
          }}
          style={{ color: "#1265c4", textDecoration: "underline", fontWeight: 700 }}
        >
          <div>{title}</div>
        </a>
      </OverlayTrigger>
      {location ? (
        <OverlayTrigger
          placement="bottom"
          delay={{ show: 250, hide: 400 }}
          overlay={renderTooltip(location)}
        >
          <span style={{ fontSize: "12px", color: "#6b7280", fontWeight: 500 }}>
            {location}
          </span>
        </OverlayTrigger>
      ) : null}
    </div>
  );
}

export function projectedRetreatTitleCell(row: LifecycleRetreat) {
  const title = retreatDisplayTitle(row);
  const location = retreatLocationLine(row);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "2px", paddingTop: "4px", paddingBottom: "4px" }}>
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 400 }}
        overlay={renderTooltip(title)}
      >
        <a
          href={`/retreatRoster?retreatId=${row.id}`}
          onClick={(e) => {
            e.preventDefault();
            window.open(`/retreatRoster?retreatId=${row.id}`, "_blank");
          }}
        >
          <div style={{ color: "#1265c4", fontWeight: 700 }}>
            {title}
          </div>
        </a>
      </OverlayTrigger>
      {location ? (
        <OverlayTrigger
          placement="bottom"
          delay={{ show: 250, hide: 400 }}
          overlay={renderTooltip(location)}
        >
          <span style={{ fontSize: "12px", color: "#6b7280", fontWeight: 500 }}>
            {location}
          </span>
        </OverlayTrigger>
      ) : null}
    </div>
  );
}

export function countLinkCell(count: number | undefined, href: string) {
  const n = count ?? 0;
  return (
    <OverlayTrigger
      placement="bottom"
      delay={{ show: 250, hide: 400 }}
      overlay={renderTooltip(String(n))}
    >
      <div className="tooltip-static-demo">
        <div className="bs-tooltip-bottom bs-tooltip-bottom-demo tooltip-info">
          <div className="arrow"></div>
          <a href={href} style={{ textDecoration: "none" }} target="_blank" rel="noopener noreferrer">
            <div className="tooltip-inner">{n}</div>
          </a>
        </div>
      </div>
    </OverlayTrigger>
  );
}

export function closureCell(row: LifecycleRetreat) {
  const conf = Array.isArray(row.confirmation) ? row.confirmation : [];
  const rosterOk = conf[0]?.roasterData === true;
  const directoryOk = conf[1]?.Directory === true;
  const programOk = conf[2]?.Program === true;
  return (
    <div className="retreat_closure">
      <div>
        <div>
          <div className={rosterOk ? "green-dot" : "red-dot"}></div>{" "}
          <span>Retreat Roster data</span>
        </div>
        {directoryOk ? (
          <a href={`/directory/${row.id}`} target="_blank" rel="noopener noreferrer">
            <div className="green-dot"></div> <span>Directory</span>
          </a>
        ) : (
          <>
            <div className="red-dot"></div> <span>Directory</span>
          </>
        )}
      </div>
      <div>
        {programOk ? (
          <a href={`/program/${row.id}`} target="_blank" rel="noopener noreferrer">
            <div className="green-dot"></div> <span>Program</span>
          </a>
        ) : (
          <>
            <div className="red-dot"></div> <span>Program</span>
          </>
        )}
      </div>
    </div>
  );
}

type ColumnHandlers = {
  isSuperAdmin: boolean;
  canEdit: boolean;
  onStatusToggle: (row: LifecycleRetreat) => void;
  onEditInactiveReason: (row: LifecycleRetreat) => void;
  onWaitlistExport: (row: LifecycleRetreat) => void;
  onDelete: (row: LifecycleRetreat) => void;
  onFreezeToggle: (row: LifecycleRetreat) => void;
  onUploadDirectory: (row: LifecycleRetreat) => void;
  onUploadProgram: (row: LifecycleRetreat) => void;
  onCreatedBy: (row: LifecycleRetreat) => void;
  onConfirmedBy: (row: LifecycleRetreat) => void;
  onHistory?: (row: LifecycleRetreat) => void;
};

export function registrationOpenColumns(h: ColumnHandlers) {
  const adminCols = h.isSuperAdmin
    ? [
      {
        id: "action",
        name: "Action",
        button: true,
        cell: (row: LifecycleRetreat) => {
          const frozen = row.freeze === "FREEZE";
          return (
            <div className="d-flex align-items-center flex-nowrap" style={{ gap: "8px" }}>
              <RegistrationOpenRowIcons
                row={row}
                frozen={frozen}
                showDelete
                onHistory={h.onHistory}
                onDelete={h.onDelete}
              />

              <OverlayTrigger
                placement="bottom"
                delay={{ show: 250, hide: 400 }}
                overlay={renderTooltip(
                  row.status === "ACTIVE"
                    ? "Status: ACTIVE (Click to Deactivate registration)"
                    : "Status: INACTIVE (Click to Activate registration)"
                )}
              >
                <Form.Check
                  className={`switchSize ${row.status === "ACTIVE" ? "toggle-on-status" : "toggle-off-status"
                    }`}
                  type="checkbox"
                  name="status"
                  value={row.status}
                  checked={row.status === "ACTIVE"}
                  onChange={() => h.onStatusToggle(row)}
                  id={`status-${row.id}`}
                />
              </OverlayTrigger>

              {row.status === "INACTIVE" && (
                <OverlayTrigger
                  placement="bottom"
                  delay={{ show: 250, hide: 400 }}
                  overlay={renderTooltip("Edit Deactivate Reason")}
                >
                  <i
                    onClick={() => h.onEditInactiveReason(row)}
                    className="mdi mdi-pencil text-lg font-size"
                    style={{ cursor: "pointer" }}
                    title="Edit Deactivate Reason"
                  ></i>
                </OverlayTrigger>
              )}

              {row.waitList === true && (
                <OverlayTrigger
                  placement="bottom"
                  delay={{ show: 250, hide: 400 }}
                  overlay={renderTooltip("Export Waitlist")}
                >
                  <span
                    className="waitlistcouple"
                    style={{ cursor: "pointer" }}
                    onClick={() => h.onWaitlistExport(row)}
                  >
                    WL
                  </span>
                </OverlayTrigger>
              )}

              <OverlayTrigger
                placement="bottom"
                delay={{ show: 250, hide: 400 }}
                overlay={renderTooltip(
                  row.freeze === "FREEZE"
                    ? "Freeze: FROZEN (Click to Unfreeze retreat)"
                    : "Freeze: UNFROZEN (Click to Freeze retreat)"
                )}
              >
                <Form.Check
                  className={`switchSize ${row.freeze === "FREEZE" ? "toggle-on" : "toggle-off"
                    }`}
                  type="switch"
                  name="freeze"
                  value={row.freeze}
                  checked={row.freeze === "FREEZE"}
                  onChange={() => h.onFreezeToggle(row)}
                  id={`freeze-${row.id}`}
                />
              </OverlayTrigger>
            </div>
          );
        },
        reorder: true,
        width: "210px",
        conditionalCellStyles: upcomingCellStyles,
      },
      {
        id: "registrationLink",
        name: "Registration Link",
        button: true,
        cell: (row: LifecycleRetreat) => (
          <a
            href={`/registration-retreat/${row.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="roasterKey"
          >
            <img
              src={row.image1 || uploadRetreat}
              className="column-rowImage"
              alt=""
              onError={(e) => {
                const img = e.currentTarget;
                img.onerror = null;
                img.src = corruptedImg;
              }}
            />
          </a>
        ),
        reorder: true,
        width: "120px",
        conditionalCellStyles: upcomingCellStyles,
      },
    ]
    : [
      ...(h.canEdit
        ? [
          {
            id: "action",
            name: "Action",
            button: true,
            cell: (row: LifecycleRetreat) => {
              const frozen = row.freeze === "FREEZE";
              return (
                <RegistrationOpenRowIcons
                  row={row}
                  frozen={frozen}
                  showDelete={false}
                  onHistory={h.onHistory}
                  onDelete={h.onDelete}
                />
              );
            },
            reorder: true,
            width: "100px",
            conditionalCellStyles: upcomingCellStyles,
          },
        ]
        : []),
      {
        id: "registrationLink",
        name: "Registration Link",
        button: true,
        cell: (row: LifecycleRetreat) => (
          <a
            href={`/registration-retreat/${row.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="roasterKey"
          >
            <img
              src={row.image1 || uploadRetreat}
              className="column-rowImage"
              alt=""
              onError={(e) => {
                const img = e.currentTarget;
                img.onerror = null;
                img.src = corruptedImg;
              }}
            />
          </a>
        ),
        reorder: true,
        width: "120px",
        conditionalCellStyles: upcomingCellStyles,
      },
    ];

  return [
    ...adminCols,
    {
      id: "retreatId",
      name: "Retreat Id",
      selector: (row: LifecycleRetreat) => row.id,
      sortable: true,
      reorder: true,
      width: "130px",
      style: { cursor: "text" },
      conditionalCellStyles: upcomingCellStyles,
    },
    {
      id: "title",
      name: "Retreat Title",
      selector: (row: LifecycleRetreat) =>
        `${row.parishName || row.title || ""}`,
      cell: retreatTitleCell,
      sortable: true,
      reorder: true,
      width: "300px",
      conditionalCellStyles: upcomingCellStyles,
    },
    {
      id: "Retreatants",
      name: "# Retreatants",
      selector: (row: LifecycleRetreat) => row.countRetreatant,
      cell: (row: LifecycleRetreat) =>
        countLinkCell(row.countRetreatant, `/retreatRoster?retreatId=${row.id}&flag=1`),
      reorder: true,
      sortable: true,
      width: "150px",
      style: { cursor: "text" },
      conditionalCellStyles: upcomingCellStyles,
    },
    {
      id: "team",
      name: "# Teams",
      selector: (row: LifecycleRetreat) => row.countTeam,
      cell: (row: LifecycleRetreat) =>
        countLinkCell(row.countTeam, `/retreatRoster?retreatId=${row.id}&flag=2`),
      reorder: true,
      sortable: true,
      width: "110px",
      style: { cursor: "text" },
      conditionalCellStyles: upcomingCellStyles,
    },
    {
      id: "Post Retreat Closure",
      name: "Post Retreat Closure",
      cell: (row: LifecycleRetreat) => closureCell(row),
      sortable: true,
      reorder: true,
      width: "170px",
      style: { cursor: "text" },
      conditionalCellStyles: upcomingCellStyles,
    },
    {
      id: "language",
      name: "Language",
      selector: (row: LifecycleRetreat) => formatLanguage(row.language),
      cell: (row: LifecycleRetreat) => tooltipCell(formatLanguage(row.language)),
      sortable: true,
      reorder: true,
      width: "120px",
      style: { cursor: "text" },
      conditionalCellStyles: upcomingCellStyles,
    },
    {
      id: "location",
      name: "Location",
      selector: (row: LifecycleRetreat) => cap(row.location),
      cell: (row: LifecycleRetreat) => tooltipCell(cap(row.location)),
      sortable: true,
      reorder: true,
      width: "130px",
      style: { cursor: "text" },
      conditionalCellStyles: upcomingCellStyles,
    },
    {
      id: "fromToDate",
      name: "Retreat Dates",
      selector: (row: LifecycleRetreat) =>
        formatRetreatDateRange(row.dateFrom, row.dateTo),
      cell: (row: LifecycleRetreat) =>
        tooltipCell(formatRetreatDateRange(row.dateFrom, row.dateTo)),
      sortable: true,
      reorder: true,
      width: "150px",
      style: { cursor: "text" },
      conditionalCellStyles: upcomingCellStyles,
    },
    {
      id: "Directory",
      name: "Directory",
      button: true,
      cell: (row: LifecycleRetreat) => {
        const frozen = row.freeze === "FREEZE";
        return (
          <div
            className={`font-size ml-3 action-icons ${row.directoryFile ? "file_side" : ""} ${frozen ? "editRetreatDisabled" : ""
              }`}
          >
            {frozen ? (
              <a>
                <i className="mdi mdi-folder-open" title="Upload Directory"></i>
              </a>
            ) : (
              <a
                onClick={(e) => {
                  e.preventDefault();
                  h.onUploadDirectory(row);
                }}
                style={{ cursor: "pointer" }}
                title="Upload Directory"
                className="font-size"
              >
                <i className="mdi mdi-folder-open"></i>
              </a>
            )}
            {row.directoryFile && (
              <a
                className="ml-2"
                title="View Directory"
                href={row.directoryFile}
                target="_blank"
                rel="noopener noreferrer"
                style={{ cursor: "pointer" }}
              >
                <i
                  className="mdi mdi-download"
                  title="View Directory"
                  style={{ color: "#4cd964", marginLeft: "0.5rem" }}
                ></i>
              </a>
            )}
          </div>
        );
      },
      reorder: true,
      width: "110px",
      conditionalCellStyles: upcomingCellStyles,
    },
    {
      id: "Program",
      name: "Program",
      button: true,
      cell: (row: LifecycleRetreat) => {
        const frozen = row.freeze === "FREEZE";
        return (
          <div
            className={`font-size ml-3 action-icons ${row.programFile ? "file_side" : ""} ${frozen ? "editRetreatDisabled" : ""
              }`}
          >
            {frozen ? (
              <a>
                <i className="mdi mdi-calendar-multiselect" title="Upload Program"></i>
              </a>
            ) : (
              <a
                onClick={(e) => {
                  e.preventDefault();
                  h.onUploadProgram(row);
                }}
                style={{ cursor: "pointer" }}
                title="Upload program"
                className="font-size"
              >
                <i className="mdi mdi-calendar-multiselect"></i>
              </a>
            )}
            {row.programFile && (
              <a
                className="ml-2"
                title="View Program"
                href={row.programFile}
                target="_blank"
                rel="noopener noreferrer"
                style={{ cursor: "pointer" }}
              >
                <i
                  className="mdi mdi-download"
                  title="View Program"
                  style={{ color: "#4cd964", marginLeft: "0.5rem" }}
                ></i>
              </a>
            )}
          </div>
        );
      },
      reorder: true,
      width: "110px",
      conditionalCellStyles: upcomingCellStyles,
    },
    {
      id: "created",
      name: "Created",
      selector: (row: LifecycleRetreat) =>
        Helpers.convertUtcToTimezone(row.createdAt || "", "MM-DD-YYYY hh:mm:a"),
      cell: (row: LifecycleRetreat) => {
        const text = Helpers.convertUtcToTimezone(row.createdAt || "", "MM-DD-YYYY hh:mm:a");
        return (
          <OverlayTrigger
            placement="bottom"
            delay={{ show: 250, hide: 400 }}
            overlay={renderTooltip(text)}
          >
            <span>{text}</span>
          </OverlayTrigger>
        );
      },
      reorder: true,
      sortable: true,
      width: "170px",
      style: { cursor: "text" },
      conditionalCellStyles: upcomingCellStyles,
    },
    {
      id: "created_by",
      name: "Created By",
      cell: (row: LifecycleRetreat) => {
        const name =
          row.contact_name && row.contact_name !== "" ? row.contact_name : "ADMIN";
        return (
          <OverlayTrigger
            placement="bottom"
            delay={{ show: 250, hide: 400 }}
            overlay={renderTooltip(name)}
          >
            {name !== "ADMIN" ? (
              <span onClick={() => h.onCreatedBy(row)} className="sampleData">
                {name}
              </span>
            ) : (
              <span>ADMIN</span>
            )}
          </OverlayTrigger>
        );
      },
      reorder: true,
      sortable: true,
      width: "130px",
      conditionalCellStyles: upcomingCellStyles,
      style: { cursor: "text" },
    },
    {
      id: "confirmed_by",
      name: "Confirmed By",
      cell: (row: LifecycleRetreat) => (
        <OverlayTrigger
          placement="bottom"
          delay={{ show: 250, hide: 400 }}
          overlay={renderTooltip("Confirmed data List")}
        >
          <span onClick={() => h.onConfirmedBy(row)}>
            <i className="mdi mdi-view-headline font-size action-icons" title="Confirmed List"></i>
          </span>
        </OverlayTrigger>
      ),
      reorder: true,
      sortable: true,
      width: "150px",
      style: { cursor: "pointer" },
      conditionalCellStyles: upcomingCellStyles,
    },
  ];
}

export function createdByName(row: LifecycleRetreat) {
  return row.contact_name && row.contact_name !== "" ? row.contact_name : "ADMIN";
}
